// Change order status wire values — shared across GC/Owner/Admin views so a
// rename never has to happen in more than one place.
//
// The backend is migrating these off the old Lender-approval naming as Change
// Order approval moves to Owner. Confirmed via live API response so far:
//   pending_bank_approval -> pending_owner_approval (confirmed)
//   bank_approved         -> owner_approved          (confirmed)
//   bank_rejected         -> owner_rejected           (inferred from the
//                                                       above pattern, NOT
//                                                       yet confirmed live —
//                                                       verify by rejecting a
//                                                       CO and checking the
//                                                       network response)
//   change_requested      -> owner_change_request     (confirmed)
// Historical change orders from before the migration may still carry the old
// values, so every comparison below accepts both.
export const CO_STATUS = {
  PENDING: "pending_owner_approval",
  APPROVED: "owner_approved",
  REJECTED: "owner_rejected",
  CHANGE_REQUESTED: "owner_change_request",
  SYSTEM_APPROVED: "system_approved",
} as const;

export type ChangeOrderStatus = (typeof CO_STATUS)[keyof typeof CO_STATUS];

export const LEGACY_CO_PENDING_STATUS = "pending_bank_approval";
export const LEGACY_CO_APPROVED_STATUS = "bank_approved";
export const LEGACY_CO_REJECTED_STATUS = "bank_rejected";
// Two older values have been seen for this same state: "bank_change_request"
// (original Lender-era wording) and "change_requested" (an intermediate
// rename before it settled on "owner_change_request").
export const LEGACY_CO_CHANGE_REQUESTED_STATUSES = [
  "bank_change_request",
  "change_requested",
];

export function isCoPending(status?: string | null): boolean {
  return status === CO_STATUS.PENDING || status === LEGACY_CO_PENDING_STATUS;
}

export function isCoApproved(status?: string | null): boolean {
  return status === CO_STATUS.APPROVED || status === LEGACY_CO_APPROVED_STATUS;
}

export function isCoRejected(status?: string | null): boolean {
  return status === CO_STATUS.REJECTED || status === LEGACY_CO_REJECTED_STATUS;
}

export function isCoChangeRequested(status?: string | null): boolean {
  return (
    status === CO_STATUS.CHANGE_REQUESTED ||
    (!!status && LEGACY_CO_CHANGE_REQUESTED_STATUSES.includes(status))
  );
}

// Rewrites any legacy status value to its current canonical equivalent, so
// callers only ever have to compare against one value per state.
export function normalizeChangeOrderStatus(
  status: string | undefined,
): string | undefined {
  if (status === LEGACY_CO_PENDING_STATUS) return CO_STATUS.PENDING;
  if (status === LEGACY_CO_APPROVED_STATUS) return CO_STATUS.APPROVED;
  if (status === LEGACY_CO_REJECTED_STATUS) return CO_STATUS.REJECTED;
  if (status && LEGACY_CO_CHANGE_REQUESTED_STATUSES.includes(status))
    return CO_STATUS.CHANGE_REQUESTED;
  return status;
}

// Values sent to the approve/reject/request-info action endpoint.
export const CO_ACTION = {
  APPROVED: "approved",
  REJECTED: "rejected",
  CHANGE_REQUEST: "change_request",
} as const;

export type ChangeOrderAction = (typeof CO_ACTION)[keyof typeof CO_ACTION];

// Single source of truth for how each status renders in a change-order log
// table (CoLogTable / cologtableSec) across every role's view.
export const CO_STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  [CO_STATUS.PENDING]: { label: "Pending owner approval", cls: "bPending" },
  [CO_STATUS.APPROVED]: { label: "Owner approved", cls: "bApproved" },
  [CO_STATUS.REJECTED]: { label: "Owner rejected", cls: "bRejected" },
  [CO_STATUS.CHANGE_REQUESTED]: {
    label: "Owner change request",
    cls: "bPending",
  },
  [CO_STATUS.SYSTEM_APPROVED]: { label: "Auto approved", cls: "bApproved" },
  draft: { label: "Draft", cls: "bPending" },
};
